/**
 * Réglages de l'application.
 *
 * Portage de `PreferencesManager.kt`. Les valeurs par défaut et les bornes
 * sont reprises du code Android, qui fait foi lorsqu'il diverge de la
 * documentation (l'espacement des points kilométriques vaut 2 km, pas 1).
 *
 * Le stockage est synchrone et tient dans `localStorage` : ce sont quelques
 * dizaines de scalaires. Les traces et les caches, eux, vont en IndexedDB.
 */

import { DEFAULT_STYLE_ID, type MapStyleId } from './mapStyles'
import { defaultSymbolForType } from './types'

const STORAGE_PREFIX = 'velogpx.'

/** Codes pays disponibles pour la base de radars. */
export const SUPPORTED_RADAR_COUNTRIES = [
  'FR',
  'CH',
  'IT',
  'ES',
  'DE',
  'BE',
] as const

export type RadarCountry = (typeof SUPPORTED_RADAR_COUNTRIES)[number]

export type LanguagePreference = 'system' | 'fr' | 'en'
export type ThemePreference = 'system' | 'light' | 'dark'

export interface Preferences {
  // --- Général ---
  language: LanguagePreference
  theme: ThemePreference

  // --- Carte ---
  mapStyle: MapStyleId
  show3dBuildings: boolean
  satelliteEnabled: boolean

  // --- Interface ---
  showZoomButtons: boolean
  showGuidanceButton: boolean
  /**
   * Diamètre de la flèche de position, en pixels.
   *
   * Enregistré en pixels et non en niveau : l'application Android n'offre que
   * cinq crans, trop grossiers pour ajuster finement la taille apparente.
   */
  locationIconSize: number
  showSpeedAltitude: boolean
  showAltitude: boolean
  /** 0 à 4 → 14, 18, 22, 26 ou 30 px. */
  speedAltitudeSize: number

  // --- Navigation ---
  /** Délai avant recentrage automatique après un geste, en secondes. */
  relockDelay: number
  kmIndicatorEnabled: boolean
  roadbookEnabled: boolean
  roadbookMarkerCountPortrait: number
  roadbookMarkerCountLandscape: number
  roadbookTextSize: number
  /** Distance après laquelle un marqueur dépassé disparaît, en km. */
  roadbookDisappearDistance: number
  markerAlertEnabled: boolean
  markerAlertDistance: number
  markerAlertSize: number
  elevationProfileHudEnabled: boolean
  elevationProfileHudRange: number
  elevationProfileHudRangeBehind: number

  // --- Marqueurs ---
  kmMarkersEnabled: boolean
  kmMarkerInterval: number
  /** Échelle des icônes ; 0,6 correspond à 100 % dans l'interface. */
  markerScale: number
  customMarkerDarkMode: boolean
  /**
   * Symboles des types prédéfinis choisis par l'utilisateur.
   *
   * Indexé par identifiant de type, et ne contient que les surcharges : un
   * type absent garde le symbole d'origine. Android tient un réglage par type
   * (`predefined_symbol_$typeId`) ; une seule entrée fait ici le même office
   * sans multiplier les clés de stockage.
   */
  predefinedSymbols: Record<string, string>

  // --- Radars ---
  radarsEnabled: boolean
  radarCountries: RadarCountry[]
  radarsLastRefresh: number

  // --- Développeur ---
  developerModeEnabled: boolean

  // --- Avertissement au lancement ---
  /**
   * L'avertissement d'usage a été lu, et l'utilisateur ne veut plus le voir.
   *
   * Faux par défaut : le message reparaît à chaque chargement tant que la case
   * n'est pas cochée. C'est un rappel de sécurité, pas un contrat qu'on signe
   * une fois — Android, lui, retient l'acceptation définitivement (écart au
   * tableau du `README.md`).
   *
   * Rangé dans `localStorage` avec les autres réglages, et non dans un cache :
   * « Vider le cache » ne touche qu'IndexedDB, si bien que la case survit à
   * une purge. L'effacement des données du site la remet à zéro, ce qui est
   * le comportement attendu.
   */
  startupWarningDismissed: boolean
}

export const DEFAULT_PREFERENCES: Readonly<Preferences> = Object.freeze({
  language: 'system',
  theme: 'system',

  mapStyle: DEFAULT_STYLE_ID,
  show3dBuildings: true,
  satelliteEnabled: false,

  showZoomButtons: true,
  showGuidanceButton: true,
  // 28 et non 32 : à valeur nominale égale, le rendu du navigateur donne une
  // flèche sensiblement plus grosse que celui d'Android. Écart constaté sur
  // appareil, la géométrie du dessin étant pourtant identique.
  locationIconSize: 28,
  showSpeedAltitude: true,
  showAltitude: true,
  speedAltitudeSize: 2,

  relockDelay: 15,
  kmIndicatorEnabled: true,
  roadbookEnabled: true,
  roadbookMarkerCountPortrait: 5,
  // 4 et non 5 comme Android : en paysage, un navigateur rend moins de hauteur
  // utile qu'une application native — barre d'adresse, barre d'onglets et
  // marges système s'y ajoutent —, et le cinquième marqueur sortait du cadre.
  // Le réglage va toujours de 1 à 10 : c'est le point de départ qui change.
  roadbookMarkerCountLandscape: 4,
  roadbookTextSize: 1,
  roadbookDisappearDistance: 1.0,
  markerAlertEnabled: true,
  markerAlertDistance: 2.0,
  markerAlertSize: 1,
  elevationProfileHudEnabled: true,
  elevationProfileHudRange: 5,
  elevationProfileHudRangeBehind: 1,

  kmMarkersEnabled: true,
  // Le code Android retourne 2.0 ; la documentation annonce 1 km à tort.
  kmMarkerInterval: 2.0,
  markerScale: 0.6,
  customMarkerDarkMode: false,
  predefinedSymbols: {} as Record<string, string>,

  radarsEnabled: false,
  radarCountries: ['FR'] as RadarCountry[],
  radarsLastRefresh: 0,

  developerModeEnabled: false,

  startupWarningDismissed: false,
})

/** Bornes appliquées à la lecture, comme les `coerceIn` du code Android. */
const RANGES: Partial<Record<keyof Preferences, { min: number; max: number }>> = {
  locationIconSize: { min: 16, max: 48 },
  speedAltitudeSize: { min: 0, max: 4 },
  relockDelay: { min: 5, max: 60 },
  roadbookMarkerCountPortrait: { min: 1, max: 10 },
  roadbookMarkerCountLandscape: { min: 1, max: 10 },
  roadbookTextSize: { min: 0, max: 4 },
  roadbookDisappearDistance: { min: 0, max: 5 },
  markerAlertDistance: { min: 0.5, max: 10 },
  markerAlertSize: { min: 0, max: 4 },
  elevationProfileHudRange: { min: 1, max: 20 },
  elevationProfileHudRangeBehind: { min: 0, max: 10 },
  kmMarkerInterval: { min: 0.5, max: 10 },
  markerScale: { min: 0.4, max: 1.2 },
}

/** Correspondance niveau → taille de texte des panneaux, en pixels. */
export const HUD_TEXT_SIZES = [14, 18, 22, 26, 30] as const

/** Correspondance niveau → taille des symboles du radar d'alerte. */
export const MARKER_ALERT_SIZES = [18, 24, 28, 32, 36] as const

/**
 * Anciens crans de taille de la flèche, repris de l'application Android.
 *
 * Conservés pour relire les réglages enregistrés avant le passage à une taille
 * exprimée en pixels.
 */
export const LEGACY_LOCATION_ICON_SIZES = [16, 24, 32, 40, 48] as const

/**
 * Tailles essayées par l'indicateur kilométrique, de la plus grande à la plus
 * petite, en pixels.
 *
 * Reprises telles quelles d'`applyKmIndicatorStyle` : 22 sp par défaut, puis
 * 18 et 14 quand le texte ne tient plus entre les deux boutons du haut. Le
 * portage fait correspondre un sp à un pixel CSS, comme partout ailleurs.
 */
export const KM_INDICATOR_FONT_SIZES = [22, 18, 14] as const

/**
 * Choisit la plus grande taille dont le texte tient dans la place disponible.
 *
 * `measure` rend la largeur du texte à la taille proposée : la mesure est
 * laissée au dessin, seule la décision vit ici. Android essaie les trois
 * tailles dans l'ordre et **retombe sur la plus petite** quand aucune ne
 * convient — le texte déborde alors, plutôt que d'être tronqué. Ce dernier cas
 * est le seul qui ne devrait jamais arriver, et donc celui qui casserait en
 * silence : une trace de plus de mille kilomètres sur un écran étroit y mène.
 */
export function fitKmIndicatorFontSize(
  availableWidth: number,
  measure: (fontSize: number) => number,
): number {
  for (const fontSize of KM_INDICATOR_FONT_SIZES) {
    if (measure(fontSize) <= availableWidth) return fontSize
  }

  return KM_INDICATOR_FONT_SIZES[KM_INDICATOR_FONT_SIZES.length - 1]!
}

export function hudTextSize(level: number): number {
  const index = Math.min(Math.max(Math.round(level), 0), HUD_TEXT_SIZES.length - 1)
  return HUD_TEXT_SIZES[index]!
}

export function markerAlertSize(level: number): number {
  const index = Math.min(
    Math.max(Math.round(level), 0),
    MARKER_ALERT_SIZES.length - 1,
  )
  return MARKER_ALERT_SIZES[index]!
}

/**
 * Convertit une valeur enregistrée en diamètre de flèche.
 *
 * Une valeur de 1 à 5 est un ancien cran : aucune flèche n'a jamais fait cinq
 * pixels, la confusion est impossible.
 */
export function locationIconSize(stored: number): number {
  if (stored >= 1 && stored <= LEGACY_LOCATION_ICON_SIZES.length) {
    return LEGACY_LOCATION_ICON_SIZES[Math.round(stored) - 1]!
  }

  return Math.min(Math.max(Math.round(stored), 16), 48)
}

/** Longueur maximale d'un symbole, comme le filtre de saisie d'Android. */
export const MAX_SYMBOL_LENGTH = 2

/**
 * Symbole effectif d'un type prédéfini.
 *
 * La surcharge l'emporte, le symbole d'origine sert de repli. Les marqueurs
 * déjà posés gardent le leur : le symbole est copié dans le marqueur à la
 * création, comme sur Android — changer le réglage ne réécrit pas l'existant.
 */
export function resolvePredefinedSymbol(
  overrides: Readonly<Record<string, string>>,
  typeId: string,
  defaultSymbol: string,
): string {
  const override = overrides[typeId]
  return override !== undefined && override.length > 0 ? override : defaultSymbol
}

/**
 * Copie neuve des valeurs par défaut.
 *
 * `Object.freeze` ne gèle que le premier niveau : partager le tableau des pays
 * ou la table des symboles exposerait les valeurs d'origine à une écriture
 * involontaire, et deux instances du magasin s'influenceraient.
 */
function freshDefaults(): Preferences {
  return {
    ...DEFAULT_PREFERENCES,
    radarCountries: ['FR'],
    predefinedSymbols: {},
  }
}

type Listener = (preferences: Readonly<Preferences>) => void

/**
 * Réglages en mémoire, adossés à `localStorage`.
 *
 * Les écritures sont immédiates : un réglage perdu au rechargement est plus
 * agaçant qu'une écriture de trop.
 */
export class PreferencesStore {
  #values: Preferences
  #listeners = new Set<Listener>()

  constructor(private readonly storage: Storage | null = safeLocalStorage()) {
    this.#values = freshDefaults()
    this.#load()
  }

  get values(): Readonly<Preferences> {
    return this.#values
  }

  get<K extends keyof Preferences>(key: K): Preferences[K] {
    return this.#values[key]
  }

  /** Symbole à donner à un marqueur du type demandé, surcharge comprise. */
  symbolForType(typeId: string): string {
    return resolvePredefinedSymbol(
      this.#values.predefinedSymbols,
      typeId,
      defaultSymbolForType(typeId),
    )
  }

  set<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
    const clamped = this.#clamp(key, value)
    if (Object.is(this.#values[key], clamped)) {
      return
    }

    this.#values = { ...this.#values, [key]: clamped }
    this.#persist(key, clamped)
    this.#emit()
  }

  /** Remet tous les réglages à leur valeur d'origine. */
  reset(): void {
    this.#values = freshDefaults()
    if (this.storage) {
      for (const key of Object.keys(DEFAULT_PREFERENCES)) {
        this.storage.removeItem(STORAGE_PREFIX + key)
      }
    }
    this.#emit()
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  #emit(): void {
    for (const listener of this.#listeners) {
      listener(this.#values)
    }
  }

  #clamp<K extends keyof Preferences>(
    key: K,
    value: Preferences[K],
  ): Preferences[K] {
    const range = RANGES[key]
    if (range && typeof value === 'number') {
      return Math.min(Math.max(value, range.min), range.max) as Preferences[K]
    }

    // Une entrée relue peut venir d'une altération manuelle : on ne garde que
    // des symboles utilisables, plutôt que de laisser un objet ou une chaîne
    // de vingt caractères se retrouver sur la carte.
    if (key === 'predefinedSymbols') {
      const entries = Object.entries(value as Record<string, unknown>).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === 'string' &&
          entry[1].length > 0 &&
          entry[1].length <= MAX_SYMBOL_LENGTH,
      )
      return Object.fromEntries(entries) as Preferences[K]
    }

    // Au moins un pays doit rester sélectionné, sans quoi la base de radars
    // n'aurait plus aucune source à rafraîchir.
    if (key === 'radarCountries') {
      const list = value as RadarCountry[]
      const filtered = list.filter((c) => SUPPORTED_RADAR_COUNTRIES.includes(c))
      return (filtered.length > 0 ? filtered : ['FR']) as Preferences[K]
    }

    return value
  }

  #persist<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
    if (!this.storage) return
    try {
      this.storage.setItem(STORAGE_PREFIX + key, JSON.stringify(value))
    } catch {
      // Quota dépassé ou mode privé : les réglages restent valables pour la
      // session en cours, ce qui vaut mieux que de faire échouer l'action.
    }
  }

  #load(): void {
    if (!this.storage) return

    const loaded: Partial<Preferences> = {}

    for (const key of Object.keys(DEFAULT_PREFERENCES) as (keyof Preferences)[]) {
      const raw = this.storage.getItem(STORAGE_PREFIX + key)
      if (raw === null) continue

      try {
        const parsed = JSON.parse(raw) as Preferences[typeof key]
        const expected = typeof DEFAULT_PREFERENCES[key]

        // `typeof null` vaut « object » : sans ce filtre, une entrée nulle
        // passerait pour une table de symboles valable.
        if (parsed === null) continue

        // Une valeur au mauvais type vient d'une version antérieure ou d'une
        // altération manuelle : on garde le défaut plutôt que de propager.
        if (typeof parsed === expected || Array.isArray(parsed)) {
          // Migration avant bornage : la taille de flèche s'est enregistrée en
          // crans de 1 à 5 jusqu'à la version précédente, et le bornage aux
          // pixels ramènerait ces valeurs au minimum plutôt qu'à leur
          // équivalent.
          const migrated =
            key === 'locationIconSize' && typeof parsed === 'number'
              ? locationIconSize(parsed)
              : parsed

          loaded[key] = this.#clamp(key, migrated) as never
        }
      } catch {
        // Entrée illisible : ignorée silencieusement.
      }
    }

    this.#values = { ...this.#values, ...loaded }
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    // Certains navigateurs lèvent une exception quand les cookies tiers
    // sont bloqués et que la page est dans une iframe.
    return null
  }
}
